/* Copyright 2012-2019 The MathWorks, Inc. */

// TODO: Remove _CSSStateMixin dependency, as seen in SplitButton and ToggleSplitButton

define([
    "dojo/_base/declare",
    "dojo/debounce",
    "dojo/dom-geometry",
    "dojo/aspect",
    "dojo/touch",
    "dojo/_base/lang",
    "dojo/on",
    "dojo/keys",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_Container",
    "dijit/_HasDropDown",
    "dijit/registry",
    "dijit/_CssStateMixin",
    "dojo/text!./templates/Gallery.html",
    "../../constants/gallery/GalleryViewConstants",
    "../_WidgetMixin",
    "./GalleryItemRowCountMixin",
    "./GalleryItemTextLineCountMixin",
    "./ScrollToSelectionMixin",
    "./HideDisabledItemsMixin",
    "mw-form/mixins/property/VisualFamilyMixin",
    "mw-form/mixins/property/DisabledMixin",
    "MW/toolstrip/mixins/property/MnemonicMixin",
    "MW/toolstrip/mixins/property/MnemonicKeyNavDirectionMixin",
    "MW/uiframework/FocusManager",
    "dojo/i18n!l10n/nls/toolstrip"
], function (declare, debounce, domGeom, aspect, touch, lang, on, keys, _WidgetBase, _TemplatedMixin, _Container,
             _HasDropDown, registry, _CssStateMixin, template, GalleryViewConstants, _WidgetMixin,
             GalleryItemRowCountMixin, GalleryItemTextLineCountMixin, ScrollToSelectionMixin, HideDisabledItemsMixin,
             VisualFamilyMixin, DisabledMixin, MnemonicMixin, MnemonicKeyNavDirectionMixin, FocusManager, toolstripL10n) {
    return declare([_WidgetBase, _TemplatedMixin, _Container, _CssStateMixin, _HasDropDown, _WidgetMixin,
        GalleryItemRowCountMixin, GalleryItemTextLineCountMixin, ScrollToSelectionMixin, HideDisabledItemsMixin,
        VisualFamilyMixin, DisabledMixin, MnemonicMixin, MnemonicKeyNavDirectionMixin], {

        templateString: template,
        baseClass: "mwWidget mwGallery",
        visualFamily: "toolstrip",
        dropDownPosition: ["below"],
        openIfEmpty: true,
        ownsPopup: true,

        /** @lends GalleryWidget# */

        /**
         * Represents a Gallery Widget. It displays the first n (maxColumnCount * rowCount)
         * items starting with the favorites and followed by the category items in order. It also
         * has a drop down arrow on the right which when clicked opens the Gallery Popup Widget.
         */

        /**
         * @override
         */
        postCreate: function () {
            this.inherited(arguments);

            // Prevent HasDropDown from managing focus
            this._focusManager = {};

            this.own(
                aspect.after(this, "addChild", function (widget) {
                    // If the item being created is going to be located in the Gallery then it will
                    // always be rendered as an Icon view.
                    widget.set("displayState", GalleryViewConstants.ICON);
                    widget.set("galleryItemRowCount", this.galleryItemRowCount);
                }, true),
                on(this.openPopupDiv, "click", function (e) {
                    // Popup does not always open from touch.press on IE11, so open on click if it failed.
                    // Also, Mnemonics relies on "click" event to open the popup.
                    // Since the first time the popup is opened, it has not been placed in a
                    // dijitPopup parent, we need to accomodate that case.
                    if (!this.isDropDownOpen()) {
                        this._onDropDownMouseDown(e);
                    }
                }.bind(this)),
                on(this.domNode, "keyup", function (e) {
                    if ((e.keyCode === keys.ENTER) || (e.keyCode === keys.SPACE)) {
                        this.openDropDown();
                    }
                }.bind(this))
            );

            var galleryPopup = registry.byId(this.galleryPopupId);
            if (galleryPopup) {
                this.setupGalleryPopupListener(galleryPopup);
            }

            this.openPopupDiv.setAttribute("title", toolstripL10n.showMore);
            var galleryTag = this.domNode.getAttribute("data-tag") || "Gallery";
            this.openPopupDiv.setAttribute("data-tag", galleryTag + ".PopupButton");
        },

        /**
         * @override from _hasDropDown
         */
        _onKey: function (e) {
            // g2146299: _hasDropDown keydown listener callback opens the gallery popup. Don't execute this callback when
            // down arrow key is pressed on the openPopupDiv.
            if (!(e.target === this.openPopupDiv && e.keyCode === keys.DOWN_ARROW)) {
                this.inherited(arguments);
            }
        },

        openDropDown: function() {
            // When creating the Gallery from MCOS, the galleryPopup widget might not have been created yet even though
            // we know the galleryPopupId. So, wait until the gallery popup open is triggered to lazily look for the
            // galleryPopup widget using the galleryPopupId
            var galleryPopupWidth,
                galleryPopupColumnCount,
                galleryPopupWidthAdjustment = 30;   

            var galleryPopup = registry.byId(this.galleryPopupId);

            // If all children are hidden, then don't open, unless
            if (!this.openIfEmpty && (galleryPopup.domNode.querySelectorAll(".Category.hideFromView").length > galleryPopup.getChildren().length)) {
                return;
            }

            // if the property columnCount is set then it has to be bigger than the currentColumnCount
            // for it to take effect as we don't want gallery popup to open smaller than the gallery view.
            if (galleryPopup.columnCount) {
                galleryPopupColumnCount = Math.max(galleryPopup.columnCount, this.currentColumnCount);
            } else {
                galleryPopupColumnCount = this.currentColumnCount;
            }

            // setting the width of the gallery popup according to the number of items present.
            if (galleryPopupColumnCount < 3) {
                galleryPopupWidth = (3 * this.galleryItemWidth);
            } else {
                galleryPopupWidth = (galleryPopupColumnCount * this.galleryItemWidth);
            }

            // magic number is to account for the openPopup button and left/right padding in the gallery view
            galleryPopupWidth +=  galleryPopupWidthAdjustment;

            galleryPopup.domNode.style.width = galleryPopupWidth + "px";
            this.set("dropDown", galleryPopup);

            this.setupGalleryPopupListener(galleryPopup);

            this.inherited(arguments);

            this._handleWindowResizeHandler =  lang.hitch(this, "_handleWindowResize");
            window.addEventListener("resize", this._handleWindowResizeHandler);
        },

        _shouldCloseDropDown: function (target, galleryPopup) {
            // If Window is blurred, then do close
            if (target === window) {
                return true;
            }

            // If target is the drop down arrow for the gallery, and the gallery is not open, then do not close
            if (this.domNode && this.domNode.contains(target) &&
                target.classList.contains("dijitDownArrowButton") &&
                (!galleryPopup.domNode || !galleryPopup.domNode.parentElement || (galleryPopup.domNode.parentElement.style.display !== "none"))) {
                return false;
            }

            // If target is a child of the Gallery Popup then do not close
            if (galleryPopup.domNode && galleryPopup.domNode.contains(target)) {
                return false;
            }

            var selector = ".mwToolstripPopupContextMenu, .mwToolstripPopupContextMenu  *";
            
            // If target is a child of the Toolstrip Popup context menu, then do not close
            if (target.matches) {
                return !target.matches(selector);
            } else if (target.matchesSelector) {
                return !target.matchesSelector(selector);
            } else if (target.webkitMatchesSelector) {
                return !target.webkitMatchesSelector(selector);
            } else if (target.mozMatchesSelector) {
                return !target.mozMatchesSelector(selector);
            } else if (target.msMatchesSelector) {
                return !target.msMatchesSelector(selector);
            } else if (target.oMatchesSelector) {
                return !target.oMatchesSelector(selector);
            }

            // Otherwise, close
            return true;
        },

        closeDropDown: function() {
            window.removeEventListener("resize", this._handleWindowResizeHandler);

            if (!this.dropDown) {
                this.dropDown  = registry.byId(this.galleryPopupId);
                // Some tests are failing because this.dropDown is undefined, but this seems due to 
                // the cleanup methods running before the listeners finish running.  Either way, we
                // can't clean up a widget we can't find, so just return.
                if (!this.dropDown) {return;}
            }

            this.inherited(arguments);
        },

        _handleWindowResize:  function () {
            this.closeDropDown();
        },

        _qeDropDownClick: function () {
            this.toggleDropDown();
        },

        _onDropDownMouseDown: function(/*Event*/ e){
            // return if mouse event and not a left click
            if((e instanceof MouseEvent) && (e.button !== 0)){
                return;
            }

            this.inherited(arguments);
        },

        setupGalleryPopupListener: function (galleryPopup) {
            galleryPopup._invokingGallery = this;
            var that = this;
            if (this._galleryPopupListener) {
                this._galleryPopupListener.remove();
                this._galleryPopupListener = null;
            }
            this._galleryPopupListener = aspect.after(galleryPopup, "galleryStateChanged", function () {
                that.refresh();
            });
        },

        shrink: function () {
            var result;

            if (this.currentColumnCount > this.minColumnCount) {
                this.set("currentColumnCount", this.currentColumnCount - 1);
                result = true;
            } else {
                result = false;
            }

            return result;
        },

        grow: function () {
            var result;

            if (this.currentColumnCount < this.maxColumnCount) {
                this.set("currentColumnCount", this.currentColumnCount + 1);
                result = true;
            } else {
                result = false;
            }

            return result;
        },

        _setCurrentColumnCountAttr: function (value) {
            this._set("currentColumnCount", value);
            this._resizeContainerNode();
            this.refresh();
        },

        _setOpenIfEmptyAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("'openIfEmpty' property expected a boolean value");
            }
            this._set("openIfEmpty", value);
        },

        _resizeContainerNode: function () {
            var width = (this.currentColumnCount * this.galleryItemWidth) + "px";
            this.containerNode.style.width = width;
            this.textOverlayWrapperNode.style.width = width;
        },

        _setGalleryItemWidthAttr: function (value) {
            if (value > 0) {
                this._set("galleryItemWidth", value);
                this._resizeContainerNode();
            }
        },

        _setDisplayStateAttr: function (value) {
            var galleryPos = domGeom.position(this.domNode);
            this._set("displayState", value);

            this.domNode.classList.remove("busy");
            this.set("disabled", false);

            if (value === "busy") {
                this.busyLayer.style.height = galleryPos.h + "px";
                this.busyLayer.style.width = galleryPos.w + "px";
                this.domNode.classList.add("busy");
            } else if (value === "blocked") {
                this.set("disabled", true);
            }

        },

        _setTextOverlayAttr: function (value) {
            this._set("textOverlay", value);

            if (value) {
                this.textOverlayWrapperNode.style.display = "table";
                this.textOverlayNode.textContent = value;
            } else {
                this.textOverlayWrapperNode.style.display = "none";
                this.textOverlayNode.textContent = "";
            }
        },

        // Follow the same pattern as dijit's _FormWidgetMixin
        focus: function () {
            if (!this.disabled && this.focusNode.focus) {
                try {
                    this.focusNode.focus();
                } catch (e) {
                }
            }
        },

        refresh: function () {
        },

        _setGalleryPopupIdAttr: function (value) {
            var galleryPopup;

			if (this.galleryPopupId && (value !== this.galleryPopupId) && (galleryPopup = registry.byId(this.galleryPopupId))) {
                // Temporarily clear the 'galleryPopupId' property so we don't end up in a bad state if error
                this._set("galleryPopupId", "");

                // Destroy the popup ourselves if we own it
                if (this.ownsPopup) {
                    if (!this.dropDown) {
                        // Only destroy the popup when the Dojo will not be able to destroy it. This happens when the popup
                        // is only created but not opened.
                        galleryPopup.destroyRecursive();
                    }
                } else {
                    this.dropDown = undefined; // Prevent Dojo from destroying the GalleryPopup since Gallery does not own GalleryPopup
                }
            }

            // Set the new galleryPopupId
            this._set("galleryPopupId", value);
        },

        destroy: function () {
            this._galleryPopupListener.remove();
            this.set("galleryPopupId", "");
            this.inherited(arguments);
        },

        isDropDownOpen: function () {
            var galleryPopup = this.galleryPopupId ? registry.byId(this.galleryPopupId) : null;

            return !!galleryPopup && // Does the galleryPopup widget exist
                !!galleryPopup.domNode && // Does it have a domNode (false = destroyed, or not built yet)
                !!galleryPopup.domNode.parentElement && // Does it have a parent (false = definitely not in DOM)
                !( // Is the galleryPopup closed (if not, then it's open)
                    ( // Before first open, the galleryPopup has not been placed in a dijitPopup container yet
                        !galleryPopup.domNode.parentElement.classList.contains("dijitPopup") &&
                        galleryPopup.domNode.style.display === "none"
                    ) ||
                    ( // dijitPopup container sets 'display = "none"' when closed
                        galleryPopup.domNode.parentElement.style.display === "none"
                    )
                );
        }
    });
});
